import { Divider } from '@material-ui/core';
import Breadcrumbs from 'components/Common/Breadcrumb';
import Loader from 'components/Loader';
import Paginate from 'components/Paginate';
import TableError from 'components/TableError';
import { showImageError } from 'containers/blog/helper';
import useRequest from 'hooks/useRequest';
import { useRouter } from 'hooks/useRouter';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
  Badge,
  Card,
  CardBody,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  Table,
  UncontrolledDropdown,
} from 'reactstrap';
import {
  achievementsLevel,
  achievementsType,
  ACTIVE_AND_DELETED_ARRAY,
  LANGUAGE_ARRAY_WITH_STRING,
  PRINT_LANGUAGE,
  PRINT_LANGUAGE_COLOR,
} from 'utils/constants';
import { buildURL, checkIfValueExist, printDate, printSerialNumbers, showImage, titleCaseIfExists } from 'utils/helper';
import { SMKLinks } from 'utils/links';

const AchievementsListing = () => {
  const router = useRouter();
  const history = useHistory();
  toast.configure();

  const [achievementsList, setAchievementsList] = useState([]);
  const [achievementsPagination, setAchievementsPagination] = useState({});

  const defaultFilters = {
    active: { value: null, label: 'All' },
    deleted: { value: null, label: 'All' },
    type: null,
    level: null,
    name: '',
    module: '',
    badge: '',
  };

  const [filters, setFilters] = useState(defaultFilters);

  const [getAchievementsListRequest, { loading: getAchievementsListLoading }] = useRequest({
    url: `/achievement/admin/search`,
    method: 'POST',
    onSuccess: (data) => {
      setAchievementsList(data?.results);
      setAchievementsPagination(data?.pagination);
    },
  });

  const [updateAchievementRequest, { loading: updateAchievementLoading }] = useRequest({
    onSuccess: (data) => {
      handleChangePage(1);
      toast.success(`Success! Achievement has been updated.`);
    },
  });

  const setPageInUrl = (page) => {
    history.push({
      pathname: router.pathname,
      search: `?${new URLSearchParams({ page }).toString()}`,
    });
  };

  const handleChangePage = (page) => {
    setPageInUrl(page);

    getAchievementsListRequest({
      body: {
        page,
        size: 20,
        sortBy: 'id',
        sortOrder: 1,
        active: checkIfValueExist(filters.active?.value),
        deleted: checkIfValueExist(filters.deleted?.value),
        ...(!isEmpty(filters.type?.value) ? { type: filters.type?.value } : {}),
        ...(!isEmpty(filters.level?.value) ? { level: filters.level?.value } : {}),
        ...(!isEmpty(filters.name) ? { name: filters.name } : {}),
        ...(!isEmpty(filters.module) ? { module: filters.module } : {}),
        ...(!isEmpty(filters.badge) ? { badge: filters.badge } : {}),
      },
    });
  };

  const applyFilters = () => {
    handleChangePage(1);
  };

  const clearFilters = () => {
    setPageInUrl(1);

    setFilters({ ...defaultFilters });
    getAchievementsListRequest({
      body: {
        page: 1,
        size: 20,
        sortBy: 'id',
        sortOrder: 1,
      },
    });
  };

  useEffect(() => {
    handleChangePage(parseInt(router?.query?.page, 10) || 1);
  }, []);

  const handleUpdateAchievement = (data, key) => {
    if (data[key]) {
      updateAchievementRequest({
        method: 'PUT',
        url: `/achievement/admin/${data?.id}`,
        body: {
          [key]: false,
        },
      });
    } else {
      updateAchievementRequest({
        method: 'PUT',
        url: `/achievement/admin/${data?.id}`,
        body: {
          [key]: true,
          ...(key === 'deleted' ? { active: false } : {}),
        },
      });
    }
  };

  return (
    <div className="page-content">
      <Loader isActive={getAchievementsListLoading || updateAchievementLoading} />
      <Container fluid>
        <Breadcrumbs title="Achievements" breadcrumbItem={`Achievements List`} />

        <Card>
          <CardBody>
            <div className="mt-4">
              <div className="row">
                <div className="col-lg-2 mb-4">
                  <label className="form-label">Active</label>
                  <Select
                    options={ACTIVE_AND_DELETED_ARRAY}
                    value={filters.active}
                    onChange={(value) => {
                      setFilters({ ...filters, active: value });
                    }}
                  />
                </div>
                <div className="col-lg-2 mb-4">
                  <label className="form-label">Deleted</label>
                  <Select
                    options={ACTIVE_AND_DELETED_ARRAY}
                    value={filters.deleted}
                    onChange={(value) => setFilters({ ...filters, deleted: value })}
                  />
                </div>

                <div className="col-lg-3 mb-4">
                  <label className="form-label">Type</label>
                  <Select
                    options={Object.keys(achievementsType)?.map((e) => ({
                      label: titleCaseIfExists(e),
                      value: achievementsType[e],
                    }))}
                    placeholder="Select type..."
                    value={filters.type}
                    onChange={(value) => setFilters({ ...filters, type: value })}
                  />
                </div>

                <div className="col-lg-3 mb-4">
                  <label className="form-label">Level</label>
                  <Select
                    options={Object.keys(achievementsLevel)?.map((e) => ({
                      label: titleCaseIfExists(e),
                      value: achievementsLevel[e],
                    }))}
                    placeholder="Select level..."
                    value={filters.level}
                    onChange={(value) => setFilters({ ...filters, level: value })}
                  />
                </div>

                <div className="col-lg-3 mb-4">
                  <label className="form-label">Name</label>
                  <input
                    className="form-control"
                    placeholder="Enter name..."
                    value={filters.name}
                    onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                  />
                </div>
                <div className="col-lg-3 mb-4">
                  <label className="form-label">Module</label>
                  <input
                    className="form-control"
                    placeholder="Enter module..."
                    value={filters.module}
                    onChange={(e) => setFilters({ ...filters, module: e.target.value })}
                  />
                </div>
                <div className="col-lg-3 mb-4">
                  <label className="form-label">Badge</label>
                  <input
                    className="form-control"
                    placeholder="Enter badge..."
                    value={filters.badge}
                    onChange={(e) => setFilters({ ...filters, badge: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="mt-4">
              <a className="btn btn-primary me-4" onClick={applyFilters}>
                Apply Filters
              </a>
              <a className="" onClick={clearFilters}>
                Clear
              </a>
            </div>
          </CardBody>
        </Card>

        <Row className="mb-2 align-items-center">
          <Col sm="4">
            <div className="search-box me-2 mb-2 d-inline-block">
              <div className="position-relative font-bold">{achievementsPagination?.totalCount} Achievements Found</div>
            </div>
          </Col>
          <Col sm="8">
            <div className="text-sm-end">
              <Link
                replace
                className="btn btn-success btn-rounded waves-effect waves-light mb-2 me-2"
                to={SMKLinks.ACHIEVEMENT_ADD}>
                <i className="mdi mdi-plus me-1" />
                Add Achievement
              </Link>
            </div>
          </Col>
        </Row>

        <Row>
          <Col lg="12">
            <div className="">
              <div className="table-responsive" style={{ minHeight: 300 }}>
                <Table className="project-list-table  table-borderless ">
                  <thead>
                    <tr className="table-warning">
                      <th scope="col">S.No.</th>
                      <th scope="col">Name</th>
                      <th scope="col">Type </th>
                      <th scope="col">Level</th>
                      <th scope="col">Module</th>
                      <th scope="col">Badge</th>
                      <th scope="col">Created Date</th>
                      <th scope="col">Active</th>
                      <th scope="col">Deleted</th>
                      <th scope="col" />
                    </tr>
                  </thead>
                  <tbody>
                    {!isEmpty(achievementsList) ? (
                      achievementsList?.map((data, index) => {
                        return (
                          <tr key={index} className={data?.active ? '' : 'bg-danger text-emphasis-warning'}>
                            <td>{printSerialNumbers(achievementsPagination)[index]}</td>
                            <td>
                              <Link
                                to={buildURL(SMKLinks.ACHIEVEMENT_EDIT, {
                                  id: data?.id,
                                })}>
                                {data?.name}
                              </Link>
                              {/* <br />
                              {data?.slug} */}
                            </td>
                            <td>{data?.type}</td>
                            <td>{data?.level}</td>
                            <td>{data?.module}</td>
                            <td>{data?.badge}</td>
                            <td>{printDate(data?.createdDate)}</td>
                            <td>
                              <Badge className={data?.active ? 'bg-success' : 'bg-danger'}>
                                {data?.active ? 'Yes' : 'No'}
                              </Badge>
                            </td>
                            <td>
                              <Badge className={data?.deleted ? 'bg-success' : 'bg-danger'}>
                                {data?.deleted ? 'Yes' : 'No'}
                              </Badge>
                            </td>
                            <td>
                              <UncontrolledDropdown>
                                <DropdownToggle href="#" className="card-drop " tag="i">
                                  <i role="button" className="mdi mdi-dots-vertical font-size-18 " />
                                </DropdownToggle>
                                <DropdownMenu className="dropdown-menu-end">
                                  <DropdownItem onClick={() => handleUpdateAchievement(data, 'active')}>
                                    <i
                                      className={`fas  ${
                                        data?.active ? 'fa-ban text-danger' : 'fa-power-off text-success'
                                      }   m-1 pe-3`}
                                    />
                                    {data?.active ? 'Disable' : 'Enable (Active)'}
                                  </DropdownItem>
                                  <DropdownItem onClick={() => handleUpdateAchievement(data, 'deleted')}>
                                    <i className="fas fa-trash-alt text-danger m-1 pe-3" />
                                    {data?.deleted ? 'Undelete' : 'Delete'}
                                  </DropdownItem>
                                  <Divider className="my-2" />

                                  <Link
                                    to={buildURL(SMKLinks.ACHIEVEMENT_EDIT, {
                                      id: data?.id,
                                    })}>
                                    <DropdownItem>
                                      <i className="fas fa-pencil-alt text-success m-1 pe-3 " />
                                      Edit
                                    </DropdownItem>
                                  </Link>
                                </DropdownMenu>
                              </UncontrolledDropdown>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <TableError />
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          </Col>
        </Row>
        <Paginate pageInfo={achievementsPagination} onChangePage={handleChangePage} />
      </Container>
    </div>
  );
};

export default AchievementsListing;
